import classNames from 'classnames';
import { EditorMiddleware } from '@picsart/design-system/components/EditorMiddleware';
import { useEnvironmentScreen } from '@picsart/design-system/hooks/styling';

import { isHostEmbed } from '@/utils/environment/surface';

import useStyles from './styles';
import type { AppLayoutProps } from './types';

/**
 * Root shell for both viewports, built on `EditorMiddleware` — the DS template
 * that owns the root-entry environment (meta viewport, editor root styles,
 * pull-to-refresh and zoom suppression).
 *
 * Mobile used to use `EditorBlank`, which DS v13 replaced with `Blank`. `Blank`
 * spreads its `header` prop into a `PanelHeading` and drops `className`, so it
 * can no longer host this app's custom `<Header>`. The mobile branch keeps the
 * old `auto 1fr` header/body grid locally instead.
 *
 * The environment is set up by calling `useEnvironmentScreen` directly — the
 * external-mount path the DS documents — rather than by passing
 * `__isRootEntryElement`. That flag drives the same hook, but it also switches
 * the section's height from `100%` to `var(--editor-root-height)`, i.e. `100vh`.
 * `useEditorRootStyles` gives `html` that same `100vh` while DS `foundation.css`
 * pads `html` by the safe-area insets under `box-sizing: border-box`, so the
 * section would ask for the full viewport inside a content box already `100vh`
 * minus those insets and overflow the shell by the bottom one. Anything sized to
 * the shell instead — the full-screen `SearchScreen` overlay — then stops that
 * far short and leaves the screen underneath showing through the gap.
 *
 * The flags below reproduce what `__isRootEntryElement` passed, except that
 * the window min-width is never enforced. The rest default to `true`.
 *
 * The meta viewport is left alone in a host embed: that page already has one,
 * rendered by the host's React, and the DS adopts a lone viewport tag and then
 * `.remove()`s it on cleanup. The host's next route change then unmounts a node
 * with no parent — `removeChild` of null — and takes the whole page down.
 *
 * The window min-width is `html { min-width: 1024px }`. This app is never a
 * full page: in the editor it runs in an iframe as wide as the miniapp panel,
 * and a panel under 1024px pushed the header's close button off-screen — the
 * desktop layout already adapts down to the mobile breakpoint on its own. In a
 * host embed it was worse: gen.ai keeps this app mounted on every route, so
 * between 768px and 1024px every page of the host site scrolled sideways.
 *
 * The mobile grid drops to a single `1fr` row when `header` is omitted: with an
 * empty header slot the body is the grid's only child and auto-placement would
 * put it in the `auto` row, leaving its height content-driven. See
 * `mobileHeaderless` in `./styles`.
 */
const AppLayout = ({
  header,
  children,
  className,
  isMobile,
}: AppLayoutProps) => {
  const classes = useStyles();

  useEnvironmentScreen({
    enableWindowMinWidth: false,
    enableMetaViewPort: !isHostEmbed(),
  });

  if (isMobile) {
    return (
      <EditorMiddleware
        className={className}
        slot={
          <div
            className={classNames(classes.mobile, {
              [classes.mobileHeaderless]: !header,
            })}
          >
            {header}
            {children}
          </div>
        }
      />
    );
  }

  return (
    <EditorMiddleware
      className={className}
      slot={
        <div className={classes.desktop}>
          {header}
          {children}
        </div>
      }
    />
  );
};

export default AppLayout;
